import { useEffect, useState } from 'react';
import { X, Trash2, AlertTriangle, BookOpen, Mic, LayoutTemplate } from 'lucide-react';
import { 
  updateProject, 
  deleteProject, 
  Project, 
  StrategyDoc, 
  getOrSeedStrategyDocs, 
  getCurrentUser 
} from '../services/firebase';

interface EditProjectModalProps {
  project: Project;
  onClose: () => void;
  onSaved: () => void;
}

export function EditProjectModal({ project, onClose, onSaved }: EditProjectModalProps) {
  // Form State
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const [systemPrompt, setSystemPrompt] = useState(project.systemPrompt || '');
  const [status, setStatus] = useState(project.status);
  const [tags, setTags] = useState(project.tags?.join(', ') || '');
  
  // Narrative Links
  const [personaId, setPersonaId] = useState(project.personaId || '');
  const [voiceId, setVoiceId] = useState(project.voiceId || '');
  const [frameworkId, setFrameworkId] = useState(project.frameworkId || '');

  // UI State
  const [strategyDocs, setStrategyDocs] = useState<StrategyDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const loadDocs = async () => {
      const user = getCurrentUser();
      if (user) {
        try {
          const docs = await getOrSeedStrategyDocs(user.uid);
          setStrategyDocs(docs);
        } catch (error) {
          console.error("Failed to load strategy docs:", error);
        }
      }
    };
    loadDocs();
  }, []);

  // Categorize docs for the UI
  const voices = strategyDocs.filter(d => d.type === 'voice');
  const personas = strategyDocs.filter(d => d.type === 'persona' || d.title.startsWith('Persona:'));
  const frameworks = strategyDocs.filter(d => d.type === 'framework');

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await updateProject(project.id, {
        name: name.trim(),
        description: description.trim(),
        systemPrompt: systemPrompt.trim(),
        status,
        personaId,
        voiceId,
        frameworkId,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      onSaved();
    } catch (error) {
      console.error("Project update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteProject(project.id);
      onSaved(); // This will tell the parent dashboard to refresh and close the modal
    } catch (error) {
      console.error("Failed to delete project:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyAdditiveStrategy = (doc: StrategyDoc) => {
    // 1. Prepend content safely
    if (!systemPrompt.includes(doc.title)) {
      setSystemPrompt(`[${doc.title.toUpperCase()}]\n${doc.content}\n\n${systemPrompt}`);
    }
    // 2. Merge Tags
    const existingTags = tags.split(',').map(t => t.trim()).filter(Boolean);
    const docTags = doc.tags || [];
    const mergedTags = Array.from(new Set([...existingTags, ...docTags]));
    setTags(mergedTags.join(', '));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-black font-display">Project Configuration</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-black transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-8 overflow-y-auto">
          
          {/* NARRATIVE ENGINE FILTERS */}
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <LayoutTemplate size={16} className="text-black" />
              <h3 className="text-sm font-bold text-black font-display tracking-tight">Narrative Engine</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Voice Selector */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-mono uppercase tracking-wider text-[#6C6864] flex items-center gap-1.5">
                  <Mic size={12} /> Brand Voice (The How)
                </label>
                <select 
                  value={voiceId} 
                  onChange={(e) => {
                    setVoiceId(e.target.value);
                    const doc = voices.find(v => v.id === e.target.value);
                    if (doc) applyAdditiveStrategy(doc);
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white outline-none focus:border-black text-sm font-medium"
                >
                  <option value="">Select a Voice...</option>
                  {voices.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
                </select>
              </div>

              {/* Framework Selector */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-mono uppercase tracking-wider text-[#6C6864] flex items-center gap-1.5">
                  <LayoutTemplate size={12} /> Framework (The Arc)
                </label>
                <select 
                  value={frameworkId} 
                  onChange={(e) => {
                    setFrameworkId(e.target.value);
                    const doc = frameworks.find(f => f.id === e.target.value);
                    if (doc) applyAdditiveStrategy(doc);
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white outline-none focus:border-black text-sm font-medium"
                >
                  <option value="">Select an Objective...</option>
                  {frameworks.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
                </select>
              </div>
            </div>

            {/* Persona Grid */}
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#6C6864] flex items-center gap-1.5">
                <BookOpen size={12} /> Professional Persona (The Who)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {personas.map(persona => (
                  <button
                    key={persona.id}
                    type="button"
                    onClick={() => {
                      setPersonaId(persona.id);
                      applyAdditiveStrategy(persona);
                    }}
                    className={`text-left p-3 border rounded-xl transition-all group ${
                      personaId === persona.id 
                        ? 'border-black bg-white ring-1 ring-black shadow-sm' 
                        : 'border-slate-200 bg-white hover:border-slate-400'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <p className="text-sm font-bold text-black">{persona.title.replace('Persona: ', '')}</p>
                    </div>
                    <p className="text-[10px] text-[#6C6864] line-clamp-1">Apply this perspective</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* STANDARD PROJECT FIELDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Project Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-black" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value as any)} className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white outline-none focus:border-black">
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between items-end mb-1">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Compiled System Instructions</label>
              <button onClick={() => setSystemPrompt('')} className="text-[10px] text-[#6C6864] hover:text-black hover:underline">Clear</button>
            </div>
            <textarea 
              value={systemPrompt} 
              onChange={e => setSystemPrompt(e.target.value)} 
              className="w-full h-40 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-xs text-black outline-none focus:border-black" 
              placeholder="Select filters above to compile instructions, or write custom notes..."
            />
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Project Goal (Description)</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full h-16 px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-black resize-none" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#6C6864]">Project Tags</label>
              <input value={tags} onChange={e => setTags(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-black" />
            </div>
          </div>

          {/* DANGER ZONE (Unchanged) */}
          <div className="mt-8 pt-6 border-t border-red-50">
            {!showDeleteConfirm ? (
              <button onClick={() => setShowDeleteConfirm(true)} className="flex items-center gap-2 text-sm font-bold text-red-500 hover:text-red-700">
                <Trash2 size={16} /> Delete Project
              </button>
            ) : (
              <div className="bg-red-50 p-4 rounded-xl border border-red-100 flex justify-between items-center">
                <span className="text-red-700 text-sm font-bold flex items-center gap-2"><AlertTriangle size={16}/> Permanent Delete?</span>
                <div className="flex gap-2">
                  <button onClick={() => setShowDeleteConfirm(false)} className="text-xs font-bold text-slate-500 px-3 py-1">Cancel</button>
                  <button onClick={handleDelete} className="bg-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-lg">Confirm</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-[#6C6864] font-bold">Cancel</button>
          <button onClick={handleUpdate} disabled={loading} className="px-6 py-2 bg-black text-white rounded-lg font-bold hover:bg-slate-800 disabled:opacity-50">
            {loading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}